import axios from 'axios';

import { GithubBranch, GithubRepo } from '../../entities/github-repo';
import { StandaloneProject } from '../../entities/standalone-project';
import { API_BASE_URL } from '../api';
import { auth } from '../firebase/firebase';

export const fetchIntegrations = async () => {
  try {
    const token = await auth.currentUser?.getIdToken();
    const response = await axios.get(`${API_BASE_URL}/api/integrations/status`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching integrations:', error);
    throw error;
  }
};

export interface IntegrationUrls {
  jiraUrl: string | null;
  trelloUrl: string | null;
}

export const fetchIntegrationUrls = async (): Promise<IntegrationUrls> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.get<IntegrationUrls>(`${API_BASE_URL}/api/integrations/urls`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const fetchJiraIntegrationStatus = async () => {
  try {
    const token = await auth.currentUser?.getIdToken();
    const response = await axios.get(`${API_BASE_URL}/api/integrations/status/jira`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching Jira integration status:', error);
    throw error;
  }
};

export const disconnectIntegration = async (integrationType: string) => {
  try {
    const token = await auth.currentUser?.getIdToken();
    const response = await axios.delete(`${API_BASE_URL}/api/auth/disconnect/${integrationType}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error disconnecting integration:', error);
    throw error;
  }
};

export const subscribeToNewEmails = async () => {
  try {
    const token = await auth.currentUser?.getIdToken();
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/google-drive/watch`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error('Error subscribing to new emails:', error);
    throw error;
  }
};

export const getGoogleAuthUrl = async () => {
  const token = await auth.currentUser?.getIdToken();
  const redirectUrl = await axios.get(`${API_BASE_URL}/api/auth/google`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return redirectUrl.data;
};

export const getJiraAuthUrl = async () => {
  const token = await auth.currentUser?.getIdToken();
  const redirectUrl = await axios.get(`${API_BASE_URL}/api/auth/jira`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return redirectUrl.data;
};

export const jiraIncrementalSync = async () => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.post(
    `${API_BASE_URL}/api/sync/jira/incremental`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return response.data;
};

export const fetchTelegramBotKey = async () => {
  const idToken = await auth.currentUser?.getIdToken();
  const response = await axios.get(`${API_BASE_URL}/api/integrations/telegram-code`, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  return response.data;
};

export const getTrelloAuthUrl = async (): Promise<{ url: string }> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.get(`${API_BASE_URL}/api/auth/trello`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Standard authorization_code flow (mirrors Jira/Google, not Trello's URL-fragment token) — the
// coding agent's GitHub integration (MVPB-67/68).
export const getGithubAuthUrl = async (): Promise<{ url: string }> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.get(`${API_BASE_URL}/api/auth/github`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// Repo/branch picker for the coding-agent launcher (MVPB-99/100) — only meaningful once GitHub is
// connected. The GitHub token never crosses into this response; integration-msa fetches from
// GitHub server-side and returns just the listing.
export const listGithubRepos = async (): Promise<GithubRepo[]> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.get<{ repos: GithubRepo[] }>(`${API_BASE_URL}/api/integrations/github/repos`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.repos;
};

export const listGithubBranches = async (owner: string, repo: string): Promise<GithubBranch[]> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.get<{ branches: GithubBranch[] }>(
    `${API_BASE_URL}/api/integrations/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/branches`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data.branches;
};

export const postTrelloToken = async (trelloToken: string): Promise<{ ok: boolean }> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.post(
    `${API_BASE_URL}/api/auth/trello/token`,
    { token: trelloToken },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
};

export interface TrelloBoard {
  id: string;
  name: string;
  url?: string;
}

export const getTrelloBoards = async (): Promise<TrelloBoard[]> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.get<{ boards: TrelloBoard[] }>(`${API_BASE_URL}/api/integrations/trello/boards`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.boards;
};

export const trelloIncrementalSync = async () => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.post(
    `${API_BASE_URL}/api/sync/trello/incremental`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return response.data;
};

export interface UserProjectsData {
  availableProjects: { id: string; name: string; key: string; mapId?: string }[]; // Jira only
  selectedProjectIds: string[];
  selectedMap?: string[];
  plan: string;
  planLimit: number | null; // Jira only — Trello syncs everything, standalone has no sync gate

  trelloBoards: TrelloBoard[];
  standaloneProjects: StandaloneProject[];
}

export const fetchUserProjects = async (): Promise<UserProjectsData> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.get(`${API_BASE_URL}/api/user/projects`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const confirmJiraProjects = async (projectIds: string[], syncFrom: string): Promise<{ success: boolean }> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.post(
    `${API_BASE_URL}/api/user/projects/confirm`,
    { projectIds, syncFrom },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
};

export const updateUserProjects = async (
  projectIds: string[],
  syncFrom?: string,
): Promise<{ success: boolean; selectedProjectIds: string[] }> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.post(
    `${API_BASE_URL}/api/user/projects/confirm`,
    { projectIds, ...(syncFrom && { syncFrom }) },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
};

export const systemCheckUp = async () => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    const response = await axios.get(`${API_BASE_URL}/api/integrations/check`, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error checking system:', error);
    throw error;
  }
};

export const getSlackAuthUrl = async () => {
  try {
    const token = await auth.currentUser?.getIdToken();
    const redirectUrl = await axios.get(`${API_BASE_URL}/api/notifications/slack/connect`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return redirectUrl.data;
  } catch (error) {
    console.error('Error getting Slack auth URL:', error);
    throw error;
  }
};
